import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  almacen,
  newOperationId,
  type OrderStatus,
  type PurchaseOrder,
} from '../api';
import {
  Boton,
  Cabecera,
  Cargando,
  ConAyuda,
  Fallo,
  filaPulsable,
  Filtro,
  Pastilla,
  Rotulo,
  Tarjeta,
  Vacio,
} from '../componentes';
import { cantidad, contados, dia, soles } from '../formato';
import type { Tono } from '../formato';
import { CuadroDeBusqueda } from '../Buscable';
import { filtrarFilas } from '../busqueda';
import { MandarOrden } from '../formularios';
import { Buscable } from '../Buscable';
import { leerCantidad, leerImporte } from '../numeros';
import { MatrizParaTeclear } from '../MatrizParaTeclear';
import { deLaTemporada, familiasEntre, insumosDeFamilias, temporadasDe } from '../prendas';
import { FiltroDeTemporada } from '../Temporada';
import { mayuscula, useVocabulario } from '../vocabulario';

const ESTADO: Record<OrderStatus, { texto: string; tono: Tono }> = {
  draft: { texto: 'Borrador', tono: 'neutro' },
  sent: { texto: 'Enviada', tono: 'marca' },
  in_transit: { texto: 'En tránsito', tono: 'aviso' },
  partial: { texto: 'Parcial', tono: 'aviso' },
  received: { texto: 'Recibida', tono: 'exito' },
  cancelled: { texto: 'Anulada', tono: 'peligro' },
};

const FILTROS: { id: OrderStatus | 'todas'; texto: string }[] = [
  { id: 'todas', texto: 'Todas' },
  { id: 'draft', texto: 'Borradores' },
  { id: 'sent', texto: 'Enviadas' },
  { id: 'in_transit', texto: 'En tránsito' },
  { id: 'partial', texto: 'Parciales' },
  { id: 'received', texto: 'Recibidas' },
  { id: 'cancelled', texto: 'Anuladas' },
];

/**
 * Lo que se le pidió a cada proveedor y qué llegó de ello.
 *
 * Lo que la orden agrega sobre recibir a secas es **la diferencia entre lo
 * pedido y lo llegado**: es la única forma de saber que faltan tres kilos antes
 * de que el camión se haya ido. Sin ella, el faltante se descubre a fin de mes
 * contando, y para entonces nadie recuerda qué traía la guía.
 */
export function Compras() {
  const clientes = useQueryClient();
  const [filtro, setFiltro] = useState<OrderStatus | 'todas'>('todas');
  const [abierta, setAbierta] = useState<string | null>(null);
  const [texto, setTexto] = useState('');

  const ordenes = useQuery({
    queryKey: ['compras'],
    queryFn: almacen.purchaseOrders,
  });

  const lista = ordenes.data ?? [];
  const visibles = filtrarFilas(
    filtro === 'todas' ? lista : lista.filter((o) => o.status === filtro),
    texto,
    (o) => [o.number, o.supplier, o.supplierContact],
  );

  const comprometido = lista
    .filter((o) => o.status === 'sent' || o.status === 'in_transit' || o.status === 'partial')
    .reduce((suma, o) => suma + Number.parseFloat(o.total), 0);

  const cuantas = (id: OrderStatus | 'todas') =>
    id === 'todas' ? lista.length : lista.filter((o) => o.status === id).length;

  return (
    <>
      <Cabecera
        titulo="Órdenes de compra"
        subtitulo="Pedidos a proveedores y su estado de entrega" ayuda="Lo que se le pidió a cada proveedor y qué llegó de ello. Sirve para saber qué falta antes de que el camión se vaya."
        acciones={<NuevaOrden onCreada={() => void clientes.invalidateQueries()} />}
      />

      <div className="cuerpo">
        {ordenes.isError ? (
          <Fallo error={ordenes.error} onReintentar={() => ordenes.refetch()} />
        ) : null}

        <div className="filtros">
          <CuadroDeBusqueda
            valor={texto}
            onCambio={setTexto}
            placeholder="Buscar número o proveedor…"
            etiquetaAccesible="Buscar orden"
          />

          <span className="separador" />

          {FILTROS.map((opcion) => (
            <Filtro
              key={opcion.id}
              texto={opcion.texto}
              cuenta={cuantas(opcion.id)}
              activo={filtro === opcion.id}
              onClick={() => setFiltro(opcion.id)}
            />
          ))}
          <span className="recuento">
            COMPROMETIDO {soles(comprometido)}
          </span>
        </div>

        {ordenes.isLoading ? (
          <Cargando que="las órdenes" />
        ) : visibles.length === 0 ? (
          <Vacio
            titulo={
              filtro === 'todas' && texto.trim() === '' ? 'Ninguna orden' : 'Nada con ese filtro'
            }
            mensaje="Una orden deja escrito qué se pidió, para poder comparar contra lo que llegue. Mientras no haya ninguna, la mercadería entra por Recepción sin orden detrás y eso también funciona."
          />
        ) : (
          <div className="tabla-envoltura">
            <table>
              <thead>
                <tr>
                  <th>Orden</th>
                  <th>Proveedor</th>
                  <th><ConAyuda ayuda="El día en que se le mandó al proveedor. Vacío mientras sea borrador.">Emitida</ConAyuda></th>
                  <th><ConAyuda ayuda="El día en que debería llegar.">Entrega</ConAyuda></th>
                  <th className="numero">Ítems</th>
                  <th className="numero">Total</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {visibles.map((orden) => (
                  <FilaDeOrden
                    key={orden.id}
                    orden={orden}
                    abierta={abierta === orden.id}
                    onAbrir={() =>
                      setAbierta(abierta === orden.id ? null : orden.id)
                    }
                    onCambio={() => void clientes.invalidateQueries()}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}

function FilaDeOrden({
  orden,
  abierta,
  onAbrir,
  onCambio,
}: {
  orden: PurchaseOrder;
  abierta: boolean;
  onAbrir: () => void;
  onCambio: () => void;
}) {
  const vocabulario = useVocabulario();
  const cambiar = useMutation({
    mutationFn: (status: 'sent' | 'in_transit' | 'cancelled') =>
      almacen.changeOrderStatus(orden.id, status),
    onSuccess: () => {
      setConfirmaAnular(false);
      onCambio();
    },
  });

  // Anular pide un segundo toque: una orden anulada no vuelve.
  const [confirmaAnular, setConfirmaAnular] = useState(false);
  const anulable = ['draft', 'sent', 'in_transit', 'partial'].includes(orden.status);

  return (
    <>
      <tr {...filaPulsable(onAbrir)}>
        <td>
          <div className="celda-nombre__texto">
            <strong className="mono">{orden.number}</strong>
            <Pastilla {...ESTADO[orden.status]} />
          </div>
        </td>
        <td>{orden.supplier}</td>
        <td className="apagado mono">
          {orden.issuedAt === null ? '—' : dia(orden.issuedAt)}
        </td>
        <td className="apagado mono">
          {orden.expectedAt === null ? '—' : orden.expectedAt}
        </td>
        <td className="numero">{orden.lineCount}</td>
        <td className="numero">{soles(orden.total)}</td>
        <td style={{ textAlign: 'right' }}>
          {orden.status === 'draft' ? (
            /*
             * Antes esto solo cambiaba el estado. Ahora abre el texto para el
             * proveedor: el sistema no dice que algo se mandó hasta que
             * alguien lo mandó.
             */
            <MandarOrden orden={orden} onEnviada={onCambio} />
          ) : (
            <span className="detalle-conmutador">
              {abierta ? 'Ocultar' : 'Ver detalle'}
            </span>
          )}
        </td>
      </tr>

      {abierta ? (
        <tr>
          <td colSpan={7} style={{ background: 'var(--superficie-sutil)' }}>
            <table style={{ minWidth: 0 }}>
              <thead>
                <tr>
                  <th>{mayuscula(vocabulario.insumo)}</th>
                  <th className="numero">Pedido</th>
                  <th className="numero">Recibido</th>
                  <th className="numero"><ConAyuda ayuda="Lo pedido que todavía no llega.">Falta</ConAyuda></th>
                  <th className="numero"><ConAyuda ayuda="El precio por unidad con el que se pidió, sin IGV.">Costo unit.</ConAyuda></th>
                </tr>
              </thead>
              <tbody>
                {orden.lines.map((line) => {
                  const falta = Number.parseFloat(line.pending);

                  return (
                    <tr key={line.supplyId}>
                      <td>{line.supply}</td>
                      <td className="numero apagado">
                        {cantidad(line.ordered)} {line.unit}
                      </td>
                      <td className="numero">
                        {cantidad(line.received)} {line.unit}
                      </td>
                      <td className={`numero ${falta > 0 ? 'negativo' : ''}`}>
                        {falta > 0 ? `${cantidad(line.pending)} ${line.unit}` : '—'}
                      </td>
                      <td className="numero apagado">{soles(line.unitCost)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {orden.status === 'sent' || anulable ? (
              <div className="formulario__fila" style={{ marginTop: 'var(--hueco-md)' }}>
                {orden.status === 'sent' ? (
                  <Boton onClick={() => cambiar.mutate('in_transit')} disabled={cambiar.isPending}>
                    Ya viene en camino
                  </Boton>
                ) : null}
                {anulable ? (
                  confirmaAnular ? (
                    <>
                      <Boton onClick={() => setConfirmaAnular(false)}>No</Boton>
                      <Boton
                        variante="peligro"
                        onClick={() => cambiar.mutate('cancelled')}
                        disabled={cambiar.isPending}
                      >
                        {cambiar.isPending ? 'Anulando…' : 'Sí, anular la orden'}
                      </Boton>
                    </>
                  ) : (
                    <Boton variante="peligro" onClick={() => setConfirmaAnular(true)}>
                      Anular
                    </Boton>
                  )
                ) : null}
              </div>
            ) : null}
            {anulable && confirmaAnular ? (
              <p className="nota-sm nota--pegada">
                {orden.status === 'partial'
                  ? 'Lo que ya llegó se queda en el almacén. Lo que faltaba deja de contarse como pedido y la reposición lo vuelve a sugerir.'
                  : 'Deja de contarse como pedido y la reposición vuelve a sugerirlo. Si ya se la mandaste al proveedor, avísale.'}
              </p>
            ) : null}
            {cambiar.isError ? <Fallo error={cambiar.error} /> : null}
          </td>
        </tr>
      ) : null}
    </>
  );
}

/** Armar una orden: proveedor, fecha esperada y qué se le pide. */
function NuevaOrden({ onCreada }: { onCreada: () => void }) {
  const vocabulario = useVocabulario();
  const [abierto, setAbierto] = useState(false);
  const [supplierId, setSupplierId] = useState('');
  const [expectedAt, setExpectedAt] = useState('');
  const [lineas, setLineas] = useState<Record<string, string>>({});
  /*
   * En qué empaque se pide cada línea: vacío es la unidad del insumo. Se pide
   * «2 rollos de 100 m» y la orden guarda 200 m; el precio que se teclea es el
   * del rollo y viaja por metro (migración 276, §2.52).
   */
  const [empaqueDe, setEmpaqueDe] = useState<Record<string, string>>({});
  const factorDe = (supplyId: string): number => {
    const elegido = empaqueDe[supplyId] ?? '';
    if (elegido === '') return 1;
    const empaque = (insumos.data ?? [])
      .find((insumo) => insumo.id === supplyId)
      ?.packages?.find((uno) => uno.id === elegido);
    return empaque === undefined ? 1 : Number.parseFloat(empaque.units);
  };
  /**
   * El precio pactado de cada línea, si se cambió. Vacío es el último costo:
   * antes se tomaba ese en silencio y no había dónde poner el que dijo el
   * proveedor por teléfono.
   */
  const [precios, setPrecios] = useState<Record<string, string>>({});
  const [todoElCatalogo, setTodoElCatalogo] = useState(false);
  const [texto, setTexto] = useState('');

  /*
   * El mismo identificador en cada intento: si «Crear» se pulsa dos veces o
   * la red se corta después de guardarse, no salen dos órdenes iguales al
   * mismo proveedor.
   */
  const [orderId, setOrderId] = useState(newOperationId);

  const proveedores = useQuery({
    queryKey: ['proveedores'],
    queryFn: almacen.suppliers,
    enabled: abierto,
  });
  const insumos = useQuery({
    queryKey: ['insumos'],
    queryFn: almacen.supplies,
    enabled: abierto,
  });

  /*
   * Pedir la prenda en matriz de tallas por colores, como se recibe
   * (`PorPrenda.tsx`). Antes se pedían doce filas «Polo básico · M · Negro»
   * casi iguales, y la que se saltaba era la que faltó en el estante. Lo
   * tecleado sigue siendo una línea por insumo: la orden no cambia.
   */
  const prendas = useQuery({ queryKey: ['prendas'], queryFn: almacen.variantFamilies, enabled: abierto });
  const [porPrenda, setPorPrenda] = useState(false);
  const [temporada, setTemporada] = useState<string | null>(null);

  const crear = useMutation({
    mutationFn: () =>
      almacen.createPurchaseOrder({
        orderId,
        supplierId,
        expectedAt: expectedAt === '' ? null : expectedAt,
        lines: Object.entries(lineas)
          .filter(([, valor]) => valor.trim() !== '')
          .map(([id, quantity]) => {
            const insumo = (insumos.data ?? []).find((i) => i.id === id);

            const factor = factorDe(id);
            const precio = leerImporte(precios[id] ?? '');

            return {
              supplyId: id,
              quantity: factor === 1 ? quantity : (leerCantidad(quantity) * factor).toFixed(3),
              // Lo pactado: el que se tecleó o, si no, el costo que el almacén
              // conoce hoy. La recepción lo trae puesto y deja corregirlo,
              // porque el mercado sube. Tecleado por empaque, viaja por unidad.
              unitCost:
                factor !== 1 && !Number.isNaN(precio)
                  ? Math.round((precio / factor) * 10000) / 10000
                  : precioDe(id, insumo?.averageCost),
            };
          }),
      }),
    onSuccess: () => {
      setAbierto(false);
      setLineas({});
      setEmpaqueDe({});
      setPrecios({});
      setTexto('');
      setOrderId(newOperationId());
      onCreada();
    },
  });

  /** El precio de una línea: el tecleado si es un número, o el último costo. */
  function precioDe(supplyId: string, ultimoCosto: string | undefined): number {
    const tecleado = leerImporte(precios[supplyId] ?? '');
    return Number.isNaN(tecleado) ? Number.parseFloat(ultimoCosto ?? '0') : tecleado;
  }

  if (!abierto) {
    return (
      <Boton variante="primario" onClick={() => setAbierto(true)}>
        Nueva orden
      </Boton>
    );
  }

  /*
   * Se ofrece lo que hace falta, no el catálogo entero.
   *
   * Quien arma una orden no repasa veinticuatro insumos: mira lo que está bajo
   * mínimo. Enseñar todo obligaría a buscar, y buscar es cuando se olvida algo.
   */
  const proveedor = (proveedores.data ?? []).find((p) => p.id === supplierId);

  /*
   * Buscar mira el catálogo entero: quien teclea «aceite» lo quiere aunque no
   * esté bajo mínimo. Y lo de este proveedor va primero, que es lo que se le
   * va a pedir.
   */
  const buscando = texto.trim() !== '';
  const familiasDeLaOrden = familiasEntre(
    prendas.data ?? [],
    new Set((insumos.data ?? []).map((insumo) => insumo.id)),
  );
  const familiasVisibles = porPrenda
    ? deLaTemporada(familiasDeLaOrden, temporada).filter(
        (familia) => !buscando || familia.name.toLowerCase().includes(texto.trim().toLowerCase()),
      )
    : [];
  const enMatriz = porPrenda ? insumosDeFamilias(familiasDeLaOrden) : new Set<string>();
  const sugeridos = filtrarFilas(
    (insumos.data ?? []).filter(
      (insumo) =>
        !enMatriz.has(insumo.id) &&
        (buscando ||
        todoElCatalogo ||
        // Bajo su mínimo según el servidor, que solo lo dice con mínimo puesto:
        // antes salía todo lo que estaba en cero, y la orden de una tienda que
        // nunca contó ofrecía el catálogo entero (8 oct). Un servidor de antes
        // no lo manda: entonces por el nivel, como siempre.
        (insumo.belowMinimum ?? (insumo.level !== 'ok' && insumo.level !== 'over')) ||
        // Lo ya puesto no se esconde al cambiar de vista.
        (lineas[insumo.id] ?? '').trim() !== ''),
    ),
    texto,
    (insumo) => [insumo.name, insumo.sku, insumo.category, insumo.supplier],
  ).sort(
    (a, b) =>
      Number(b.supplier === proveedor?.name) - Number(a.supplier === proveedor?.name),
  );

  const listos = Object.values(lineas).filter((v) => v.trim() !== '').length;
  const total = Object.entries(lineas)
    .filter(([, valor]) => !Number.isNaN(leerImporte(valor)))
    .reduce((suma, [id, valor]) => {
      const insumo = (insumos.data ?? []).find((i) => i.id === id);
      return suma + leerImporte(valor) * precioDe(id, insumo?.averageCost);
    }, 0);

  /** Lo que el almacén sugiere reponer y nadie ha puesto todavía. */
  const sinPedir = sugeridos.filter(
    (insumo) =>
      (lineas[insumo.id] ?? '').trim() === '' && Number.parseFloat(insumo.suggestedRestock) > 0,
  );

  return (
    <div className="velo" role="dialog" aria-label="Nueva orden de compra">
      <Tarjeta className="modal">
        <h2 className="tarjeta__titulo">Nueva orden de compra</h2>

        {proveedores.isLoading || insumos.isLoading ? (
          <Cargando que="los datos" />
        ) : (
          <div className="formulario">
            <div className="formulario__fila">
              <div className="campo">
                <Rotulo htmlFor="proveedor" ayuda="A quién se le hace el pedido. Solo aparecen los proveedores dados de alta.">Proveedor</Rotulo>
                <Buscable
                  id="proveedor"
                  valor={supplierId}
                  onCambio={setSupplierId}
                  placeholder="Busca el proveedor…"
                  opciones={(proveedores.data ?? []).map((p) => ({
                    valor: p.id,
                    etiqueta: p.name,
                    detalle: `entrega en ${contados(p.leadTimeDays, 'día', 'días')}`,
                  }))}
                />
              </div>
              <div className="campo">
                <Rotulo htmlFor="entrega" ayuda="El día en que debería llegar. Si no llega, la orden queda en camino y se nota.">Entrega esperada</Rotulo>
                <input
                  id="entrega"
                  type="date"
                  value={expectedAt}
                  onChange={(e) => setExpectedAt(e.target.value)}
                />
              </div>
            </div>

            <div className="filtros">
              <p className="versal">Qué pedir</p>
              <CuadroDeBusqueda
                valor={texto}
                onCambio={setTexto}
                placeholder="Buscar en todo el catálogo…"
                etiquetaAccesible={`Buscar ${vocabulario.insumo} para la orden`}
              />
              <Filtro
                texto="Solo lo que falta"
                activo={!todoElCatalogo && !buscando}
                onClick={() => {
                  setTodoElCatalogo(false);
                  setTexto('');
                }}
              />
              <Filtro
                texto="Todo el catálogo"
                activo={todoElCatalogo && !buscando}
                onClick={() => {
                  setTodoElCatalogo(true);
                  setTexto('');
                }}
              />
              {sinPedir.length > 0 ? (
                <button
                  type="button"
                  className="enlace-sutil"
                  onClick={() =>
                    setLineas((previo) => ({
                      ...previo,
                      ...Object.fromEntries(
                        sinPedir.map((insumo) => [
                          insumo.id,
                          String(Number.parseFloat(insumo.suggestedRestock)),
                        ]),
                      ),
                    }))
                  }
                >
                  Pedir lo sugerido ({contados(sinPedir.length, 'línea', 'líneas')})
                </button>
              ) : null}
              {familiasDeLaOrden.length > 0 ? (
                <Filtro texto={vocabulario.porFamilia} activo={porPrenda} onClick={() => setPorPrenda(!porPrenda)} />
              ) : null}
            </div>
            {porPrenda ? (
              <FiltroDeTemporada temporadas={temporadasDe(familiasDeLaOrden)} elegida={temporada} alElegir={setTemporada} />
            ) : null}
            {familiasVisibles.map((familia) => {
              const insumosDeLaPrenda = [...insumosDeFamilias([familia])];
              return (
                <section key={familia.id} className="tarjeta prenda">
                  <div className="prenda__cabeza">
                    <h3 className="tarjeta__titulo">{familia.name}</h3>
                    {/* Un precio para todas las tallas: el proveedor cobra la
                        prenda, no la M negra aparte. Se reparte a cada línea. */}
                    <input
                      className="celda-input"
                      inputMode="decimal"
                      aria-label={`Precio de ${familia.name} por ${vocabulario.pieza}, sin IGV`}
                      title="S/ por pieza, sin IGV, para todas. Vacío: el último costo de cada una"
                      placeholder="S/ —"
                      value={precios[insumosDeLaPrenda[0]!] ?? ''}
                      onChange={(e) =>
                        setPrecios((previo) => ({
                          ...previo,
                          ...Object.fromEntries(insumosDeLaPrenda.map((id) => [id, e.target.value])),
                        }))
                      }
                    />
                  </div>
                  <MatrizParaTeclear
                    familia={familia}
                    valores={lineas}
                    rotulo="Cuánto pedir de"
                    alTeclear={(supplyId, valor) => setLineas((previo) => ({ ...previo, [supplyId]: valor }))}
                    placeholderDe={(supplyId) => {
                      const sugerido = Number.parseFloat(
                        (insumos.data ?? []).find((insumo) => insumo.id === supplyId)?.suggestedRestock ?? '0',
                      );
                      return sugerido > 0 ? cantidad(String(sugerido)) : undefined;
                    }}
                  />
                </section>
              );
            })}
            {sugeridos.length === 0 && porPrenda ? null : sugeridos.length === 0 ? (
              <p className="aviso-caja">
                {buscando
                  ? `Ningún ${vocabulario.insumo} «${texto.trim()}» en el catálogo.`
                  : 'Nada está bajo mínimo ahora mismo. Se puede pedir igual: elige «Todo el catálogo».'}
              </p>
            ) : (
              <>
              <div className="lista-cabecera" aria-hidden="true">
                <span>{mayuscula(vocabulario.insumo)}</span>
                <span>Cantidad</span>
                <span>S/ sin IGV</span>
              </div>
              <ul className="lista">
                {sugeridos.map((insumo) => (
                  <li key={insumo.id} className="lista__fila">
                    <div className="lista__texto">
                      <strong>{insumo.name}</strong>
                      <span className="apagado">
                        Tiene {cantidad(insumo.quantity)} {insumo.unit} · mínimo{' '}
                        {cantidad(insumo.minimum)}
                        {insumo.supplier === null ? '' : ` · ${insumo.supplier}`}
                      </span>
                    </div>
                    {(insumo.packages ?? []).length === 0 ? null : (
                      <select
                        className="celda-input"
                        aria-label={`En qué se pide ${insumo.name}`}
                        value={empaqueDe[insumo.id] ?? ''}
                        onChange={(e) =>
                          setEmpaqueDe((previo) => ({ ...previo, [insumo.id]: e.target.value }))
                        }
                      >
                        <option value="">En {insumo.unit}</option>
                        {(insumo.packages ?? []).map((empaque) => (
                          <option key={empaque.id} value={empaque.id}>
                            {empaque.name}
                          </option>
                        ))}
                      </select>
                    )}
                    <input
                      className="celda-input"
                      inputMode="decimal"
                      aria-label={`Cuánto pedir de ${insumo.name}, en ${insumo.unit}`}
                      title={`Cuánto pedir, en ${insumo.unit}`}
                      placeholder={cantidad(insumo.suggestedRestock)}
                      value={lineas[insumo.id] ?? ''}
                      onChange={(e) =>
                        setLineas((previo) => ({
                          ...previo,
                          [insumo.id]: e.target.value,
                        }))
                      }
                    />
                    <input
                      className="celda-input"
                      inputMode="decimal"
                      aria-label={`Precio de ${insumo.name} por ${insumo.unit}, sin IGV`}
                      title={`S/ por ${insumo.unit}, sin IGV. Vacío: el último costo`}
                      placeholder={
                        Number.parseFloat(insumo.averageCost) > 0
                          ? `S/ ${Number.parseFloat(insumo.averageCost).toFixed(2)}`
                          : 'S/ —'
                      }
                      value={precios[insumo.id] ?? ''}
                      onChange={(e) =>
                        setPrecios((previo) => ({
                          ...previo,
                          [insumo.id]: e.target.value,
                        }))
                      }
                    />
                  </li>
                ))}
              </ul>
              </>
            )}

            {crear.isError ? <Fallo error={crear.error} /> : null}

            <div className="formulario__fila">
              <Boton onClick={() => setAbierto(false)}>Cancelar</Boton>
              <Boton
                variante="primario"
                onClick={() => crear.mutate()}
                disabled={supplierId === '' || listos === 0 || crear.isPending}
              >
                {crear.isPending
                  ? 'Creando…'
                  : `Crear con ${contados(listos, 'ítem', 'ítems')}${listos > 0 ? ` · ${soles(total)}` : ''}`}
              </Boton>
            </div>
          </div>
        )}
      </Tarjeta>
    </div>
  );
}
